import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { fmtInt, fmtSigned } from "@/lib/format";

function TerminalTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-zinc-700 bg-black px-3 py-2 font-mono">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-[11px] tabular-nums">
          <span className="inline-block h-2 w-2" style={{ background: p.color }} />
          <span className="text-zinc-400 uppercase tracking-wider">{p.name}</span>
          <span className="ml-auto text-zinc-100">
            {p.dataKey === "Price" ? p.value : fmtSigned(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function COTChart({ market, history, loading }) {
  const data =
    history?.rows?.map((r) => ({
      date: r.report_date,
      "Net Non-Comm": r.net_noncomm,
      "Net Commercial": r.net_comm,
      "Open Interest": r.open_interest,
      Price: r.price ?? null,
    })) || [];
  const priceLabel = history?.price_label;
  const hasPrice = !!priceLabel && data.some((d) => d.Price != null);

  // Positioning-vs-price divergence over the last ~12 weeks.
  let divergence = null;
  if (hasPrice && data.length >= 6) {
    const w = data.slice(-12).filter((d) => d.Price != null);
    if (w.length >= 4) {
      const np = w[w.length - 1]["Net Non-Comm"] - w[0]["Net Non-Comm"];
      const pp = w[w.length - 1].Price - w[0].Price;
      if (np !== 0 && pp !== 0) {
        divergence = Math.sign(np) !== Math.sign(pp) ? "diverge" : "confirm";
      }
    }
  }

  return (
    <div className="border border-zinc-800 bg-[#09090B] h-full" data-testid="cot-chart">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-1">
        <div className="text-[11px] uppercase tracking-widest text-zinc-400">
          ▍ Speculator Net Positioning · {market ? `${market.symbol} — ${market.name}` : "—"}
        </div>
        <div className="flex items-center gap-2">
          {divergence && (
            <span
              data-testid="pos-price-divergence"
              className={`text-[11px] uppercase tracking-widest px-1.5 py-0.5 border ${
                divergence === "diverge"
                  ? "border-amber-500/60 text-amber-term"
                  : "border-emerald-500/50 text-pos"
              }`}
              title="Spec positioning trend vs price trend over the last ~12 weeks"
            >
              {divergence === "diverge" ? "⚠ POS/PRICE DIVERGENCE" : "POS CONFIRMS PRICE"}
            </span>
          )}
          <div className="text-[11px] uppercase tracking-widest text-zinc-600">
            {history?.rows?.length ? `${history.rows.length} weeks` : ""}
          </div>
        </div>
      </div>
      <div className="p-2 h-[320px]">
        {loading || !market ? (
          <div className="h-full flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
            {market ? "Loading history…" : "Select a market"}
          </div>
        ) : data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
            No history available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                stroke="#27272A"
                tickFormatter={(d) => (d ? d.slice(5) : "")}
                minTickGap={28}
              />
              <YAxis
                yAxisId="left"
                tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                stroke="#27272A"
                tickFormatter={(v) => fmtInt(v)}
                width={70}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }}
                stroke="#27272A"
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}K` : v)}
                width={50}
              />
              <Tooltip content={<TerminalTooltip />} cursor={{ stroke: "#F59E0B", strokeDasharray: "2 2" }} />
              <Legend
                wrapperStyle={{
                  fontSize: 10,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#A1A1AA",
                  fontFamily: "JetBrains Mono",
                }}
                iconType="square"
              />
              <ReferenceLine y={0} yAxisId="left" stroke="#3F3F46" strokeDasharray="2 2" />
              <Bar
                yAxisId="right"
                dataKey="Open Interest"
                fill="#27272A"
                opacity={0.7}
                barSize={8}
              />
              <Line
                yAxisId="left"
                type="linear"
                dataKey="Net Non-Comm"
                stroke="#06B6D4"
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: "#06B6D4" }}
              />
              <Line
                yAxisId="left"
                type="linear"
                dataKey="Net Commercial"
                stroke="#F59E0B"
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: "#F59E0B" }}
              />
              {hasPrice && (
                <YAxis yAxisId="price" orientation="right" hide domain={["auto", "auto"]} />
              )}
              {hasPrice && (
                <Line
                  yAxisId="price"
                  type="linear"
                  dataKey="Price"
                  name={priceLabel || "Price"}
                  stroke="#E879F9"
                  strokeWidth={1.6}
                  strokeDasharray="4 2"
                  dot={false}
                  connectNulls
                  activeDot={{ r: 3, fill: "#E879F9" }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
